import React, { useEffect, useState, useMemo } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { QuotesSection } from '../../../../AutoDocExtraction/QuotesSection/QuotesSection';
import { deriveExtractionSuggestion } from '../deriveExtractionSuggestion';
import { DebouncedTextInput } from '../../shared/BlockWidgets';
import * as styles from './SuggestibleTextField.module.scss';
import { VisuallyHidden } from 'components/core';
import { Inline } from 'components/layout';
import { TextFieldBlock } from 'concepts/Extraction/Blocks/TextField/TextFieldBlock';
import { HydratedBlockProps } from 'concepts/Extraction/Blocks/BaseBlock';
import { useReviewFeatures } from 'hooks/reviews/useReviewFeatures';
import { isFeatureEnabledForField } from 'concepts/AutoDocExtraction/fieldFlags';
import { SuggestionActionButtons } from 'concepts/AutoDocExtraction/SuggestionActionButtons/SuggestionActionButtons';
import { useAutoDocExtSuggestion } from 'concepts/AutoDocExtraction/useAutoDocExtSuggestion';
import {
  useAutoDocExtSuggestionContext,
  getSingleExtraction,
} from 'concepts/AutoDocExtraction/AutoDocExtSuggestionProvider';
import {
  trackSuggestionDecision,
  trackSuggestionViewed,
} from 'concepts/Extraction/Extract/tracking';
import { SuggestionTrigger } from 'concepts/AutoDocExtraction/tracking';
import { usePrevious } from 'hooks/usePrevious';
import { getDE2SuggestionStatusStorageKey } from 'concepts/AutoDocExtraction/suggestionStatusStorage';

export type SuggestibleTextFieldProps = {
  block: TextFieldBlock & HydratedBlockProps;
  value: string | null;
  onChange: (newValue: string) => void;
  // Accessibility ids supplied by the parent <TextField> so the rendered
  // input is associated with its visible <LabelText> and description block.
  // Forwarded to whichever input we end up rendering (plain or readonly preview).
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

function moveCaretToEnd(el: HTMLInputElement | HTMLTextAreaElement) {
  el.focus();
  const end = el.value.length;
  el.setSelectionRange?.(end, end);
}

const PlainTextField = ({
  block,
  value,
  onChange,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: SuggestibleTextFieldProps) => (
  <DebouncedTextInput
    id={block.id}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    aria-labelledby={ariaLabelledBy}
    aria-describedby={ariaDescribedBy}
  />
);

// Renders the suggestion preview + Accept / Reject buttons when the AI has
// produced a suggestion, otherwise the regular DebouncedTextInput. Mounted
// only when the feature flag is on AND the block has an autoDocExtField, so
// it can safely consult the AutoDocExtSuggestionProvider and the document
// viewer context.
const SuggestibleTextFieldEnabled = ({
  block,
  value,
  onChange,
  autoDocExtField,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: SuggestibleTextFieldProps & { autoDocExtField: string }) => {
  const {
    reviewId,
    studyId,
    documentId,
    allSuggestions,
  } = useAutoDocExtSuggestionContext();
  const autoDocumentExtraction = getSingleExtraction(
    allSuggestions,
    autoDocExtField
  );
  const { suggestion } = deriveExtractionSuggestion(autoDocumentExtraction);

  // Suggestions are produced per (study, document) pair — a sponsor-source
  // rejection on doc A shouldn't hide a (potentially different) suggestion
  // for the same field on doc B. So the rejection bucket is keyed by
  // documentId, not studyId. When no document is selected the suggestion
  // can't have come from one anyway, so we gate the UI off entirely.
  const hasStorageAnchor = documentId !== undefined;
  const storageKey = getDE2SuggestionStatusStorageKey({ reviewId, documentId });

  const {
    showSuggestion,
    accept,
    reject,
    suggestionStatus,
  } = useAutoDocExtSuggestion({
    fieldName: block.id,
    suggestion,
    currentValue: value,
    enabled: hasStorageAnchor,
    storageKey,
  });

  // Set when acceptance is triggered from the readonly preview input itself
  // (click or Enter) so the editable input that replaces it can grab focus —
  // the user is already engaging with the field and shouldn't need a second
  // click to start editing.
  const [shouldFocusEditable, setShouldFocusEditable] = useState(false);

  const trackingData = useMemo(
    () => ({
      field: autoDocExtField,
      field_alias: block.label,
      field_type: block.type,
      review_id: reviewId,
      study_id: studyId,
      document_id: documentId ?? null,
      suggestion,
    }),
    [
      autoDocExtField,
      reviewId,
      studyId,
      documentId,
      suggestion,
      block.label,
      block.type,
    ]
  );

  // Fire `metadata_suggestion_viewed` exactly once each time the suggestion
  // UI transitions into being shown
  const wasPreviouslyShowingSuggestion = usePrevious(showSuggestion);
  useEffect(() => {
    if (!wasPreviouslyShowingSuggestion && showSuggestion && suggestion) {
      trackSuggestionViewed(trackingData);
    }
  }, [wasPreviouslyShowingSuggestion, showSuggestion, suggestion]);

  // `trigger` records whether acceptance came from the suggestion-styled
  // readonly input (click or Enter) or from the dedicated Accept button.
  const handleAccept = (trigger: SuggestionTrigger) => {
    if (trigger === 'input') setShouldFocusEditable(true);
    accept();
    if (suggestion) onChange(suggestion);
    trackSuggestionDecision('accepted', { ...trackingData, trigger });
  };

  const handleReject = () => {
    reject();
    trackSuggestionDecision('rejected', { ...trackingData, trigger: 'button' });
  };

  // Relies on PlainTextField/DebouncedTextInput rendering the textarea with
  // id={block.id}. React Hook Form's setFocus (which is used for this in DE1)
  // isn't an option here — TextFieldRender
  // drives the form via setValue/useWatch and never register()s the textarea,
  // so RHF has no ref to focus.
  useEffect(() => {
    if (!showSuggestion && shouldFocusEditable) {
      const el = document.getElementById(block.id) as
        | HTMLInputElement
        | HTMLTextAreaElement
        | null;
      if (el) {
        moveCaretToEnd(el);
      }
      setShouldFocusEditable(false);
    }
  }, [showSuggestion, shouldFocusEditable, block.id]);

  const quotesSection = (
    <QuotesSection
      autoDocExtField={autoDocExtField}
      trackedField={autoDocExtField}
      suggestionStatus={suggestionStatus}
      studyId={studyId}
      extractionTool="extraction2"
    />
  );

  if (!showSuggestion || !suggestion) {
    return (
      <>
        <PlainTextField
          block={block}
          value={value}
          onChange={onChange}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
        />
        {quotesSection}
      </>
    );
  }

  const helperId = `${block.id}-suggestion-helper`;
  const describedBy = [helperId, ariaDescribedBy].filter(Boolean).join(' ');

  return (
    <>
      <Inline spacing={2} alignY="top">
        <VisuallyHidden id={helperId}>
          Suggested value. Press Enter to accept, or tab to the Accept and
          Reject buttons.
        </VisuallyHidden>
        <TextareaAutosize
          id={block.id}
          readOnly
          aria-readonly
          aria-labelledby={ariaLabelledBy}
          aria-describedby={describedBy}
          value={suggestion || ''}
          className={styles.suggestedField}
          onClick={() => handleAccept('input')}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleAccept('input');
          }}
        />
        <SuggestionActionButtons
          suggestionLabel={suggestion}
          fieldLabel={block.label}
          size="md"
          onAccept={() => handleAccept('button')}
          onReject={handleReject}
        />
      </Inline>
      {quotesSection}
    </>
  );
};

const SuggestibleTextField = (props: SuggestibleTextFieldProps) => {
  const { autoDocExtField } = props.block;
  const { hasFeature } = useReviewFeatures();

  if (
    autoDocExtField !== undefined &&
    isFeatureEnabledForField(autoDocExtField, hasFeature)
  ) {
    return (
      <SuggestibleTextFieldEnabled
        {...props}
        autoDocExtField={autoDocExtField}
      />
    );
  }

  return <PlainTextField {...props} />;
};

export default SuggestibleTextField;
